/* eslint-disable jsx-a11y/label-has-associated-control */
import { Empty, Spin } from 'antd';

import { EmptyIcon } from 'assets/icon/bigicon';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { fetchMediaCalendar } from 'state/actions/mediaTimeline';
import './MediaTimeline.css';
import TimelineWrapper from './TimelineWrapper';
import MediaTimelineToolbar from './MediaTimelineToolbar';

dayjs.extend(utc);
dayjs.extend(timezone);

const VIEW_MODES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
};

const MS_PER_DAY = 86400000;

// Calendar keys are always `YYYY-MM-DD`, so adjacency is integer arithmetic on
// the epoch day number. The previous check parsed and reformatted with dayjs
// once per media *entry* - a yearly range is ~365 days x N media, so that alone
// was thousands of date parses per load.
const toDayNumber = (day) =>
  Date.UTC(+day.slice(0, 4), +day.slice(5, 7) - 1, +day.slice(8, 10)) /
  MS_PER_DAY;

/**
 * The API answers with a day-keyed map, so a media that runs for N days is
 * repeated N times. Collapse that expansion:
 *
 * - Media with their own absolute start/end render the exact same bar on every
 *   day they span, so only the first copy is kept.
 * - All-day media are genuinely per-day, so consecutive days are merged into a
 *   single run (a gap starts a new run).
 *
 * Without this a yearly view produces ~365 stacked cards per media.
 */
const normalizeCalendarData = (data = {}) => {
  const occurrences = [];
  const openRuns = new Map();

  Object.keys(data)
    .sort()
    .forEach((calendarDate) => {
      const dayNumber = toDayNumber(calendarDate);

      (data[calendarDate] || []).forEach((media) => {
        const isAllDay = media.type === 'all' || !media.start || !media.end;

        // categoryId is part of the identity: the same media placed in two
        // playlists arrives twice per day, and without it both copies collide
        // on one key and keep cutting each other's run down to a single day.
        const key = [
          isAllDay ? 'all' : 'fixed',
          media._id,
          media.index ?? '',
          media.categoryId ?? '',
          isAllDay ? '' : `${media.start}|${media.end}`,
        ].join('|');

        const run = openRuns.get(key);

        if (run) {
          if (!isAllDay) return;

          // Duplicate entry on a day the run already covers - keep the run.
          if (run.endDayNumber === dayNumber) return;

          if (run.endDayNumber + 1 === dayNumber) {
            run.endDayNumber = dayNumber;
            run.calendarEndDate = calendarDate;
            run.end = `${calendarDate}T23:59:59.999Z`;
            return;
          }
        }

        const next = {
          ...media,
          isAllDay,
          calendarDate,
          calendarEndDate: calendarDate,
          endDayNumber: dayNumber,
          start: media.start ?? `${calendarDate}T00:00:00.000Z`,
          end: media.end ?? `${calendarDate}T23:59:59.999Z`,
        };

        occurrences.push(next);
        openRuns.set(key, next);
      });
    });

  return occurrences;
};

// Normalized results keyed by the exact range that produced them. A yearly
// range is the expensive one to fetch, and switching view mode or stepping
// prev/next and back re-requests a range that was already paid for. The cache
// is served immediately and still revalidated in the background, so a stale
// entry can only ever be visible for as long as the refetch takes.
const CALENDAR_CACHE_LIMIT = 12;
const calendarCache = new Map();

const readCache = (key) => {
  if (!calendarCache.has(key)) return undefined;

  // Re-insert so the Map's insertion order stays LRU.
  const value = calendarCache.get(key);
  calendarCache.delete(key);
  calendarCache.set(key, value);

  return value;
};

const writeCache = (key, value) => {
  calendarCache.delete(key);
  calendarCache.set(key, value);

  if (calendarCache.size > CALENDAR_CACHE_LIMIT) {
    calendarCache.delete(calendarCache.keys().next().value);
  }
};

const MediaTimelineComponents = ({ searchTimeline, selectedCategory }) => {
  /* =========================
     STATE
  ========================= */
  const [isDailyHours, setIsDailyHours] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [viewMode, setViewMode] = useState(VIEW_MODES.DAILY);
  const [expandedPlaylist, setExpandedPlaylist] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const siteId = window.location.pathname.split('/')[1];

  const pickerType = {
    [VIEW_MODES.DAILY]: 'date',
    [VIEW_MODES.WEEKLY]: 'date',
    [VIEW_MODES.MONTHLY]: 'month',
    [VIEW_MODES.YEARLY]: 'year',
  }[viewMode];

  const getToolbarTitle = () => {
    switch (viewMode) {
      case VIEW_MODES.DAILY:
        return dayjs(selectedDate).format('DD MMMM');

      case VIEW_MODES.WEEKLY: {
        const start = dayjs(selectedDate);
        const end = start.add(6, 'day');

        return `${start.format('DD MMM')} - ${end.format('DD MMM')}`;
      }

      case VIEW_MODES.MONTHLY:
        return dayjs(selectedDate).format('MMMM YYYY');

      case VIEW_MODES.YEARLY:
        return dayjs(selectedDate).format('YYYY');

      default:
        return '';
    }
  };
  /* =========================
     DATE RANGE
  ========================= */
  const getDateRange = () => {
    const start = dayjs(selectedDate);
    const end = dayjs(selectedDate);

    switch (viewMode) {
      case VIEW_MODES.WEEKLY:
        return {
          start: start.format('YYYY-MM-DD'),
          end: start.add(6, 'day').format('YYYY-MM-DD'),
        };

      case VIEW_MODES.MONTHLY:
        return {
          start: start.startOf('month').format('YYYY-MM-DD'),
          end: start.endOf('month').format('YYYY-MM-DD'),
        };

      case VIEW_MODES.YEARLY:
        return {
          start: start.startOf('year').format('YYYY-MM-DD'),
          end: start.endOf('year').format('YYYY-MM-DD'),
        };

      case VIEW_MODES.DAILY:
      default:
        return {
          start: start.format('YYYY-MM-DD'),
          end: end.format('YYYY-MM-DD'),
        };
    }
  };

  /* =========================
     FETCH DATA
  ========================= */
  const { start: rangeStart, end: rangeEnd } = getDateRange();

  useEffect(() => {
    const controller = new AbortController();
    const cacheKey = `${siteId}|${rangeStart}|${rangeEnd}`;
    const cached = readCache(cacheKey);

    if (cached) {
      setCalendarData(cached);
    }

    // Holding prev/next fires a request per click; debounce so only the range
    // the user settles on is fetched, and abort whatever is still in flight so
    // a slow earlier response can never overwrite a newer one.
    const timer = setTimeout(async () => {
      setLoading(true);

      try {
        const res = await dispatch(
          fetchMediaCalendar({
            siteId,
            start: rangeStart,
            end: rangeEnd,
            signal: controller.signal,
          })
        );

        const normalized = normalizeCalendarData(res.data);

        writeCache(cacheKey, normalized);
        setCalendarData(normalized);
        setLoading(false);
      } catch (error) {
        if (!controller.signal.aborted) {
          setCalendarData([]);
          setLoading(false);
        }
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [dispatch, siteId, rangeStart, rangeEnd]);

  /* =========================
     GROUPING
  ========================= */
  const groupedPlaylists = useMemo(() => {
    return Object.values(
      calendarData.reduce((acc, item) => {
        if (!acc[item.categoryId]) {
          acc[item.categoryId] = {
            id: item.categoryId,
            name: item.categoryName,
            medias: [],
          };
        }
        acc[item.categoryId].medias.push(item);
        return acc;
      }, {})
    );
  }, [calendarData]);

  /* =========================
     FILTER + SEARCH
  ========================= */
  const categoryId = selectedCategory === 'all' ? '' : selectedCategory;

  const keyword = searchTimeline?.trim().toLowerCase() || '';

  const finalData = useMemo(() => {
    const filtered = groupedPlaylists.filter((p) =>
      !categoryId ? true : p.medias.some((m) => m.categoryId === categoryId)
    );

    return filtered.map((p) => {
      const medias = p.medias.map((m) => ({
        ...m,
        isMatched: !keyword || m.name.toLowerCase().includes(keyword),
      }));

      return {
        ...p,
        medias,
        hasMatched: medias.some((m) => m.isMatched),
      };
    });
  }, [groupedPlaylists, categoryId, keyword]);

  const handleDateChange = (value) => {
    if (!value) return;
    setSelectedDate(value.toDate());
    setPickerOpen(false);
  };

  const handlePrev = () => {
    setSelectedDate((prev) => {
      const date = dayjs(prev);

      switch (viewMode) {
        case VIEW_MODES.DAILY:
          return date.subtract(1, 'day').toDate();

        case VIEW_MODES.WEEKLY:
          return date.subtract(1, 'week').toDate();

        case VIEW_MODES.MONTHLY:
          return date.subtract(1, 'month').toDate();

        case VIEW_MODES.YEARLY:
          return date.subtract(1, 'year').toDate();

        default:
          return prev;
      }
    });
  };

  const handleNext = () => {
    setSelectedDate((prev) => {
      const date = dayjs(prev);

      switch (viewMode) {
        case VIEW_MODES.DAILY:
          return date.add(1, 'day').toDate();

        case VIEW_MODES.WEEKLY:
          return date.add(1, 'week').toDate();

        case VIEW_MODES.MONTHLY:
          return date.add(1, 'month').toDate();

        case VIEW_MODES.YEARLY:
          return date.add(1, 'year').toDate();

        default:
          return prev;
      }
    });
  };
  // Only a filter change invalidates the expansion. Resetting on every
  // calendarData change collapsed the row the user just opened on each refetch.
  useEffect(() => {
    setExpandedPlaylist(null);
  }, [searchTimeline, selectedCategory]);
  return (
    // position/z-index create a self-contained stacking context so the
    // timeline's internal high z-index layers (sticky toolbar/header) stay
    // trapped here and cannot cover the parent header's "Show All" dropdown.
    <div className="media-timeline" style={{ position: 'relative', zIndex: 0 }}>
      <div
        className={`media-timeline-container ${
          loading && finalData.length > 0 ? 'is-refetching' : ''
        }`}
      >
        <MediaTimelineToolbar
          date={{
            pickerOpen,
            setPickerOpen,
            pickerType,
            selectedDate,
            handleDateChange,
            handlePrev,
            handleNext,
            getToolbarTitle,
            isDailyHours,
            setIsDailyHours,
          }}
          view={{
            viewMode,
            setViewMode,
            VIEW_MODES,
          }}
        />

        {/* Only an empty *settled* result is "No Data" - during a refetch the
            previous rows stay on screen so navigation does not flash empty. */}
        {finalData.length === 0 && !loading && (
          <div className="template-empty">
            <Empty image={<EmptyIcon />} description="No Data" />
          </div>
        )}

        {finalData.length === 0 && loading && (
          <div className="template-empty">
            <Spin />
          </div>
        )}

        {finalData.length > 0 && (
          <TimelineWrapper
            groupedPlaylists={finalData}
            searchTimeline={searchTimeline}
            expandedPlaylist={expandedPlaylist}
            setExpandedPlaylist={setExpandedPlaylist}
            selectedDate={selectedDate}
            viewMode={viewMode}
            siteId={siteId}
            isDailyHours={isDailyHours}
          />
        )}
      </div>
    </div>
  );
};

export default MediaTimelineComponents;